import * as stylex from '@stylexjs/stylex'
import { cloneElement, type ComponentPropsWithRef, type ReactElement, type ReactNode } from 'react'

import { styles } from './editorial.styles'

const articleSpacing = {
  section: styles.sectionSpacing,
  subsection: styles.subsectionSpacing,
  compact: styles.compactSpacing,
  lead: styles.leadSpacing,
}

type NativeProps<Tag extends 'h2' | 'p' | 'span' | 'div' | 'a' | 'ul' | 'ol' | 'hr' | 'img'> = Omit<
  ComponentPropsWithRef<Tag>,
  'className' | 'style' | 'color'
>

export type EditorialPageProps = {
  eyebrow: ReactNode
  title?: string | undefined
  subtitle?: string | undefined
  byline?: ReactNode
  leading?: ReactNode
  intro?: ReactNode
  children: ReactNode
  variant?: 'article' | 'contact'
}

/**
 * Use EditorialPage for the public reading column, introductory content, and paper palette.
 * Choose PageLayout for a product workspace.
 * Applications supply content, bylines, artwork, and navigation adapters.
 *
 * Status: supported.
 */
export function EditorialPage({
  eyebrow,
  title,
  subtitle,
  byline,
  leading,
  intro,
  children,
  variant = 'article',
}: EditorialPageProps) {
  const Header = variant === 'contact' ? 'div' : 'header'

  return (
    <main {...stylex.props(styles.page)}>
      {leading}
      <div {...stylex.props(styles.frame, variant === 'contact' && styles.contactFrame)}>
        <Header {...stylex.props(styles.header)}>
          <div {...stylex.props(styles.eyebrow)}>{eyebrow}</div>
          {title && <h1 {...stylex.props(styles.title, variant === 'contact' && styles.contactTitle)}>{title}</h1>}
          {subtitle && <p {...stylex.props(styles.subtitle)}>{subtitle}</p>}
          {byline && <p {...stylex.props(styles.byline)}>{byline}</p>}
          {intro}
        </Header>
        {variant === 'contact' ? (
          children
        ) : (
          <>
            <EditorialDivider spacing="header" aria-hidden="true" />
            <article {...stylex.props(styles.content)}>{children}</article>
          </>
        )}
      </div>
    </main>
  )
}

type EditorialHeadingOptions = {
  level?: 2 | 3 | 4
  variant?: 'section' | 'subsection' | 'compact' | 'lead'
  spacing?: 'article' | 'directory' | 'media' | 'none'
  anchor?: 'article' | 'role'
}

export type EditorialHeadingProps = NativeProps<'h2'> & EditorialHeadingOptions

/**
 * Use EditorialHeading for article sections, directories, and media headings.
 * Choose Heading for product pages.
 * Applications select the semantic level, provide anchor identifiers, and choose the surrounding reading rhythm.
 *
 * Status: supported.
 */
export function EditorialHeading({
  level = 2,
  variant = 'section',
  spacing: separation = 'article',
  anchor,
  ...props
}: EditorialHeadingProps) {
  const Tag = `h${level}` as const

  return (
    <Tag
      {...props}
      {...stylex.props(
        styles.heading,
        styles[variant],
        separation === 'article' && articleSpacing[variant],
        separation === 'directory' && styles.directorySpacing,
        separation === 'media' && styles.mediaSpacing,
        anchor === 'article' && styles.anchored,
        anchor === 'role' && styles.roleAnchor
      )}
    />
  )
}

type EditorialTextOptions = {
  variant?: 'body' | 'supporting' | 'description' | 'event' | 'metadata' | 'fine'
}

type EditorialParagraphElement = { as?: 'p' }

type EditorialSpanElement = { as: 'span' }

type EditorialDivisionElement = { as: 'div' }

export type EditorialTextProps = EditorialTextOptions &
  (
    | (NativeProps<'p'> & EditorialParagraphElement)
    | (NativeProps<'span'> & EditorialSpanElement)
    | (NativeProps<'div'> & EditorialDivisionElement)
  )

/**
 * Use EditorialText for public prose, descriptions, metadata, and fine print.
 * Choose Text for product interface copy.
 * Applications supply content and choose the semantic element that fits the document.
 *
 * Status: supported.
 */
export function EditorialText(props: EditorialTextProps) {
  if (props.as === 'span') {
    const { as: Tag, variant = 'body', ...nativeProps } = props

    return <Tag {...nativeProps} {...stylex.props(styles.text, styles[variant])} />
  }

  if (props.as === 'div') {
    const { as: Tag, variant = 'body', ...nativeProps } = props

    return <Tag {...nativeProps} {...stylex.props(styles.text, styles[variant])} />
  }

  const { as: Tag = 'p', variant = 'body', ...nativeProps } = props

  return <Tag {...nativeProps} {...stylex.props(styles.text, styles[variant])} />
}

type EditorialLinkOptions = {
  render?: ReactElement<NativeProps<'a'>>
  variant?: 'prose' | 'back'
}

export type EditorialLinkProps = NativeProps<'a'> & EditorialLinkOptions

/**
 * Use EditorialLink for a prose destination or a back link in public reading content.
 * Choose ButtonLink for prominent navigation or Link within product copy.
 * Applications supply the destination and any router-owned anchor through render.
 *
 * Status: supported.
 */
export function EditorialLink({ render, variant = 'prose', children, ...props }: EditorialLinkProps) {
  const linkProps = { ...props, ...stylex.props(styles.link, variant === 'back' && styles.backLink) }

  return render ? cloneElement(render, linkProps, children) : <a {...linkProps}>{children}</a>
}

type UnorderedEditorialList = { ordered?: false }

type OrderedEditorialList = { ordered: true }

export type EditorialListProps =
  | (NativeProps<'ul'> & UnorderedEditorialList)
  | (NativeProps<'ol'> & OrderedEditorialList)

/**
 * Use EditorialList for ordered or unordered reading content.
 * Choose Table for comparable records or Stack for layout without list semantics.
 * Applications supply list items and choose whether their order is meaningful.
 *
 * Status: supported.
 */
export function EditorialList(props: EditorialListProps) {
  if (props.ordered) {
    const { ordered, ...nativeProps } = props

    return <ol {...nativeProps} {...stylex.props(styles.list, ordered && styles.ordered)} />
  }

  const { ordered, ...nativeProps } = props

  return <ul {...nativeProps} {...stylex.props(styles.list, !ordered && styles.unordered)} />
}

type EditorialDividerOptions = { spacing?: 'article' | 'header' | 'directory' | 'chapter' }

export type EditorialDividerProps = NativeProps<'hr'> & EditorialDividerOptions

/**
 * Use EditorialDivider to separate article, header, directory, or chapter regions with their reading rhythm.
 * Choose Separator for product content or spacing alone when the separation adds no meaning.
 * Applications select the context and declare decorative dividers through native attributes.
 *
 * Status: supported.
 */
export function EditorialDivider({ spacing: separation = 'article', ...props }: EditorialDividerProps) {
  return (
    <hr
      {...props}
      {...stylex.props(
        styles.divider,
        separation === 'header' && styles.headerDivider,
        separation === 'directory' && styles.directoryDivider,
        separation === 'chapter' && styles.chapterDivider
      )}
    />
  )
}

type EditorialImageDescription = { alt: string }

export type EditorialImageProps = Omit<NativeProps<'img'>, 'alt'> & EditorialImageDescription

/**
 * Use EditorialImage for an image within the public reading column.
 * Keep framework-specific image loading in the application and use its image component when that behavior is required.
 * Applications supply the source, alternative text, and loading attributes.
 *
 * Status: supported.
 */
export function EditorialImage({ alt, ...props }: EditorialImageProps) {
  return <img {...props} alt={alt} {...stylex.props(styles.image)} />
}

type CardAnchorProps = Omit<NativeProps<'a'>, 'children' | 'title'>

type EditorialCardContent = {
  render?: ReactElement<CardAnchorProps>
  title: string
  description: string
  trailing: ReactNode
}

type EditorialArticleCardOptions = { variant?: 'article'; titleAs?: 'p' | 'h2'; metadata: ReactNode }

type EditorialContactCardOptions = { variant: 'contact'; titleAs?: never; metadata?: never }

export type EditorialCardLinkProps = CardAnchorProps &
  EditorialCardContent &
  (EditorialArticleCardOptions | EditorialContactCardOptions)

/**
 * Use EditorialCardLink for a linked article summary or compact contact destination.
 * Choose Card for product content that does not represent one navigation target.
 * Applications supply the destination, copy, trailing content, and article metadata or contact details.
 *
 * Status: supported.
 */
export function EditorialCardLink({
  render,
  title,
  description,
  trailing,
  variant = 'article',
  titleAs: Title = 'p',
  metadata,
  ...props
}: EditorialCardLinkProps) {
  const linkProps = {
    ...props,
    ...stylex.props(styles.card, variant === 'article' ? styles.articleCard : styles.contactCard),
  }

  const content =
    variant === 'contact' ? (
      <>
        <span {...stylex.props(styles.cardContent, styles.contactContent)}>
          <span {...stylex.props(styles.cardTitle, styles.contactCardTitle)}>{title}</span>
          <span {...stylex.props(styles.contactDetail)}>{description}</span>
        </span>
        <span {...stylex.props(styles.cardTrailing, styles.contactTrailing)}>{trailing}</span>
      </>
    ) : (
      <>
        <div {...stylex.props(styles.cardContent)}>
          <div {...stylex.props(styles.cardHeadingGroup)}>
            <Title {...stylex.props(styles.cardTitle)}>{title}</Title>
            <p {...stylex.props(styles.cardSubtitle)}>{description}</p>
          </div>
          <p {...stylex.props(styles.cardMeta)}>{metadata}</p>
        </div>
        <div {...stylex.props(styles.cardTrailing)}>{trailing}</div>
      </>
    )

  return render ? cloneElement(render, linkProps, content) : <a {...linkProps}>{content}</a>
}
